/**
 * ============================================================================
 * react useModal
 * Copyright (C) 2026 LST
 * ============================================================================
 * Single-file modal library.
 * ============================================================================
 */

import React, {
    type ComponentType,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";


export type ModalCloseReason =
    | "confirm"
    | "cancel"
    | "backdrop"
    | "escape"
    | "dismiss"
    | "programmatic";

export interface ModalResult<TResult = void>
{
    reason: ModalCloseReason;
    data?: TResult;
}

export interface UseModalOptions
{
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    testId?: string;
}

export interface ModalContentProps<TInput = void, TResult = void>
{
    isOpen: boolean;
    input: TInput;
    options: UseModalOptions;
    close: (result?: ModalResult<TResult>) => void;
    confirm: (data: TResult) => void;
    cancel: () => void;
}

export type ModalComponent<TInput = void, TResult = void> =
    ComponentType<ModalContentProps<TInput, TResult>>;

export interface ModalController<TInput = void, TResult = void>
{
    open: (input: TInput) => Promise<ModalResult<TResult>>;
    close: (result?: ModalResult<TResult>) => void;
    isOpen: boolean;
    element: ReactNode;
}

export interface ErrorModalInput
{
    title?: ReactNode;
    message: ReactNode;
    details?: ReactNode;
    confirmText?: ReactNode;
}

export interface ErrorModalResult
{
    acknowledged: true;
}

export interface ShowErrorOptions
{
    title?: ReactNode;
    message: ReactNode;
    error?: unknown;
}

function createDefaultResult<TResult>(result?: ModalResult<TResult>): ModalResult<TResult>
{
    return result ?? { reason: "programmatic" };
}

export function isConfirmed<TResult>(
    result: ModalResult<TResult>,
): result is ModalResult<TResult> & { reason: "confirm"; data: TResult; }
{
    return result.reason === "confirm";
}

export function isCanceled(result: ModalResult<unknown>): boolean
{
    return result.reason === "cancel"
        || result.reason === "backdrop"
        || result.reason === "escape"
        || result.reason === "dismiss"
        || result.reason === "programmatic";
}

export function useModal<TInput = void, TResult = void>(
    Component: ModalComponent<TInput, TResult>,
    options?: UseModalOptions,
): ModalController<TInput, TResult>
{
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState<TInput | null>(null);
    const resolverRef = useRef<((result: ModalResult<TResult>) => void) | null>(null);

    const close = useCallback((result?: ModalResult<TResult>) =>
    {
        setIsOpen(false);

        const resolver = resolverRef.current;
        resolverRef.current = null;
        resolver?.(createDefaultResult(result));
    }, []);

    const open = useCallback((nextInput: TInput) =>
    {
        if (resolverRef.current) {
            resolverRef.current({ reason: "programmatic" });
            resolverRef.current = null;
        }

        setInput(nextInput);
        setIsOpen(true);

        return new Promise<ModalResult<TResult>>((resolve) =>
        {
            resolverRef.current = resolve;
        });
    }, []);

    const element = useMemo(() =>
    {
        if (input === null) {
            return null;
        }

        return (
            <Component
                isOpen={isOpen}
                input={input}
                options={options ?? {}}
                close={close}
                confirm={(data) => close({ reason: "confirm", data })}
                cancel={() => close({ reason: "cancel" })}
            />
        );
    }, [Component, close, input, isOpen, options]);

    return {
        open,
        close,
        isOpen,
        element,
    };
}

export const ErrorModal: React.FC<ModalContentProps<ErrorModalInput, ErrorModalResult>> = (
    props,
) =>
{
    const { isOpen, input, options, confirm, close } = props;
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() =>
    {
        if (isOpen) {
            containerRef.current?.focus();
        }
    }, [isOpen]);

    // useEffect(() =>
    // {
    //     if (!isOpen) {
    //         return;
    //     }

    //     const previousOverflow = document.body.style.overflow;
    //     document.body.style.overflow = "hidden";

    //     return () =>
    //     {
    //         document.body.style.overflow = previousOverflow;
    //     };
    // }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const modal = (
        <div
            ref={containerRef}
            role="dialog"
            aria-modal="true"
            aria-hidden={!isOpen}
            data-testid={options.testId}
            tabIndex={-1}
            onClick={() =>
            {
                if (options.closeOnBackdrop) {
                    close({ reason: "backdrop" });
                }
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
            {
                if (event.key === "Escape" && options.closeOnEscape) {
                    close({ reason: "escape" });
                }
            }}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 1000,
                background: "rgba(0, 0, 0, 0.45)",
                overflowY: "auto",
                overscrollBehavior: "contain",
                boxSizing: "border-box",
                padding: 16,
            }}
        >
            <div
                style={{
                    minHeight: "calc(100dvh - 32px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div
                    onClick={(event) => event.stopPropagation()}
                    style={{
                        width: "min(800px, 100%)",
                        maxHeight: "calc(100dvh - 32px)",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 12,
                        background: "white",
                        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
                        overflow: "hidden",
                        boxSizing: "border-box",
                    }}
                >
                    <div
                        style={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #e5e7eb",
                            fontWeight: 700,
                            flexShrink: 0,
                        }}
                    >
                        {input.title ?? "Error"}
                    </div>

                    <div
                        style={{
                            padding: 20,
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                        }}
                    >
                        <div style={{ marginBottom: input.details ? 12 : 0 }}>
                            {input.message}
                        </div>

                        {input.details ? (
                            <pre
                                style={{
                                    margin: 0,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    fontSize: 12,
                                    lineHeight: 1.4,
                                    padding: 12,
                                    borderRadius: 8,
                                    background: "#f3f4f6",
                                    overflow: "auto",
                                    maxHeight: "50dvh",
                                    boxSizing: "border-box",
                                }}
                            >
                                {input.details}
                            </pre>
                        ) : null}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            padding: 16,
                            borderTop: "1px solid #e5e7eb",
                            flexShrink: 0,
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => confirm({ acknowledged: true })}
                        >
                            {input.confirmText ?? "OK"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};
